import { EventsOverviewPageData, ExtractSwarovskiPageData } from "@swo/storefront";
import { AppliedFiltersT } from "./events-overview-page-layout";
import { Eq } from "fp-ts/Eq";
import { FilterBarFilter, FilteredLabeledDateValue, FilteredLabeledStringValue } from "./EventsOverviewFilterBar";
import { array, nonEmptyArray, option, ord } from "fp-ts";
import { pipe } from "fp-ts/lib/function";
import { EventsOverviewPageTranslation } from "@swo/storefront";
import { getDualOrd, Ord, ordNumber } from "fp-ts/Ord";
import { NonEmptyArray } from "fp-ts/NonEmptyArray";
import { EventT } from "@swo/salesforce-service";

type Event = ExtractSwarovskiPageData<EventsOverviewPageData>["data"]["events"][0];

export function getFilteredEvents(events: Array<Event>, appliedFilters: AppliedFiltersT) {
  let filtered: Array<Event> = events;

  const { countryFilter, dateFilter } = appliedFilters;

  if (countryFilter) {
    filtered = filtered.filter(event => {
      return event.country === countryFilter.filterValue;
    });
  }

  if (dateFilter) {
    filtered = filtered.filter(event => {
      return isDateInBetween(new Date(event.startDate), new Date(event.endDate), dateFilter.filterValue);
    });
  }

  return filtered;
}

export function monthsLength(earlier: Date, later: Date) {
  const yearsDiff = later.getFullYear() - earlier.getFullYear();
  const monthsDiff = later.getMonth() - earlier.getMonth();

  return monthsDiff + 12 * yearsDiff;
}

function isDateInBetween(from: Date, to: Date, check: Date) {
  const betweenFromAndCheck = monthsLength(from, check);
  const isNotEarlierThanFrom = betweenFromAndCheck >= 0;

  if (isNotEarlierThanFrom) {
    return betweenFromAndCheck <= monthsLength(from, to);
  } else {
    return false;
  }
}

export const stringFilterEq: Eq<FilteredLabeledStringValue> = {
  equals: (x, y) => x.value === y.value,
};

function getDateFilterLabel(d: Date, localeWithInternationalFallback: string) {
  return d.toLocaleString(localeWithInternationalFallback, { month: "long", year: "numeric" });
}

export const dateFilterEq: (
  localeWithInternationalFallback: string,
) => Eq<FilteredLabeledDateValue> = localeWithInternationalFallback => ({
  equals: (x, y) =>
    getDateFilterLabel(x.value, localeWithInternationalFallback) ===
    getDateFilterLabel(y.value, localeWithInternationalFallback),
});

const dateOrd: Ord<Date> = ord.contramap<number, Date>(o => o.getTime())(ordNumber);
const reversedDateOrd: Ord<Date> = getDualOrd(dateOrd);

export function getFilterBarFilter(
  events: Array<EventT & { link: string }>,
  translations: EventsOverviewPageTranslation,
  localeWithInternationalFallback: string,
): FilterBarFilter {
  return {
    date: {
      topLabel: translations.monthFilterLabel.value,
      values: pipe(
        nonEmptyArray.fromArray(events),
        option.map(nea => getDateFiltersWithFilledGaps(nea, localeWithInternationalFallback)),
        option.getOrElseW(() => []),
      ),
    },
    country: {
      topLabel: translations.countryFilterLabel.value,
      values: pipe(
        events
          .map(e => e.country)
          .map(country => ({
            __tag: "string" as const,
            label: country,
            value: country,
          })),
        array.uniq(stringFilterEq),
      ),
    },
  };
}

function getDateFiltersWithFilledGaps(
  events: NonEmptyArray<EventT & { link: string }>,
  localeWithInternationalFallback: string,
) {
  const eq = dateFilterEq(localeWithInternationalFallback);

  const existingEarliestDate = pipe(
    events,
    nonEmptyArray.map(e => new Date(e.startDate)),
    nonEmptyArray.sort(dateOrd),
    nonEmptyArray.head,
  );

  const now = new Date();

  const earliestDate = existingEarliestDate
    ? monthsLength(now, existingEarliestDate) < 0
      ? now
      : existingEarliestDate
    : now;

  const latestDate = pipe(
    events,
    nonEmptyArray.map(e => new Date(e.endDate)),
    nonEmptyArray.sort(reversedDateOrd),
    nonEmptyArray.head,
  );

  const monthsCount = monthsLength(earliestDate, latestDate);

  const datesInBetween = new Array(monthsCount)
    .fill(undefined)
    .map((_, idx) => idx + 1)
    .map(idx => {
      const d = new Date(earliestDate.getTime()).setMonth(earliestDate.getMonth() + idx);

      return new Date(d);
    });

  const allDays = [earliestDate, ...datesInBetween, latestDate];

  return pipe(
    allDays.map(m => ({
      __tag: "date" as const,
      label: m.toLocaleString(localeWithInternationalFallback, { month: "long", year: "numeric" }),
      value: m,
    })),
    array.uniq(eq),
  );
}
